import { get } from "lodash"
import createModule from "../createModule"
import { Creators as DialogCreators } from "modules/dialog/model"
import { getUnmodeledDates } from "selectors/dates"
import { getViewType, getPortfolioType, getPortfolioId, getSelectedModelId } from "modules/portfolio-root/selectors/portfolios"
import { getEarliestDate, getLatestDate } from "modules/portfolio-root/selectors/dates"
import { Creators as OptimizationTradesCreators } from "modules/optimization-trades/model"
import { Creators as EditExperimentCreators } from "modules/edit-experiment/model"
import { getBasePositionSet, getBaseSignature } from "selectors/base"
import { getContentSets } from "selectors/securities-table"
import view from "./view"
import { Creators as RebalanceCreators } from "modules/rebalance/model"
import { Creators } from "./model"
import { getApiConstraints, getCurrentConstraints, getCurrentHorizon, getSelectedCustomForecast, getActivatedConstraints, getExperimentSecuritiesConstraints } from "./selectors/constraints"
import { getRebalanceUrl, getOptimizeDate, getSelectedExperiment, getForecasts } from "./selectors"
import { getPortfolioData, getRiskTarget } from "./selectors/data"
import { getPortfolioNetLong } from "./selectors/market-values"
import { getRebalanceTrades, getTrades, getExperiment, getAllPositions } from "./selectors/trades"
import { generateClassificationQueryForContributors, getClassifications } from "helpers/classification"
import { VIEW_TYPES } from "helpers/base"
import { FACTOR_SENSITIVITY_TYPES, factorSensitivityParametersString } from "helpers/factorSensitivity"
import { getOptimizationContentSetsQueryNodes } from "helpers/contentSet"
import { getIsOpPro } from "modules/account-root/selectors"
import { MODEL_METADATA_QUERY } from "resources/models"

export default createModule({
  externalProps: state => ({
    accountId: state.global.selected.accountId,
    portfolioUUID: getPortfolioId(state),
    portfolioId: state.global.selected.portfolio,
    account: state.global.selected.account,
    modelId: getSelectedModelId(state),
    selectedDate: getOptimizeDate(state),
    base: getBasePositionSet(state),
    baseSignature: getBaseSignature(state),
    viewType: getViewType(state),
    horizon: getCurrentHorizon(state),
    selectedCustomForecast: getSelectedCustomForecast(state),
    contentSets: getContentSets(state),
    classifications: getClassifications(state),
    forecasts: getForecasts(state), // implicitly needed by getSelectedCustomForecast
  }),
  required: ["account", "portfolioId", "modelId", "selectedDate", "forecasts"],
  data: [{
    resource: "portfolioData",
    as: null,
    url: ({ modelId, portfolioId, selectedDate, viewType, base, baseSignature, contentSets, classifications }) => {
      baseSignature = viewType === VIEW_TYPES.ACTIVE ? baseSignature : ""
      base = viewType === VIEW_TYPES.ACTIVE ? base : null

      return {
        id: `portfolioData-${modelId}${portfolioId}${selectedDate}${baseSignature}`,
        query: `query ($modelId: String!, $portfolioId: String!, $on: Date!, $base: [PositionSetInput]) {
          portfolio (id: $portfolioId) {
            dates (from: $on, to: $on, modelId: $modelId) {
              equity
              gmv
              equities {
                id {
                  ticker
                  mic
                  modelProviderId
                  internalId
                  figi
                  bloombergTicker
                }
                economicExposure
                country
                currency
                ${generateClassificationQueryForContributors("GICS", classifications)}
              }
              currencies { id economicExposure }
              swaps { id economicExposure }
              indexes { id economicExposure }
              fixedIncome { id { isin } economicExposure }
              commodities { id economicExposure }
              otherAssets { id economicExposure }
            }
          }
          model (id: $modelId) {
            portfolio (id: $portfolioId) {
              coverage (from: $on, to: $on) {
                summary {
                  percentGmvAvailable
                }
                missingEquities {
                  id{
                    ticker
                    mic
                    modelProviderId
                  }
                }
                missingCurrencies{
                  id
                }
                missingSwaps{
                  id
                }
                missingCommodities{
                  id
                }
                missingFixedIncome{
                  id {
                    isin
                  }
                }
              }
            }
            simulation (positionSet: {
              id: $portfolioId,
              type: PORTFOLIO
            }, base: $base, from: $on, to: $on) {
              composition {
                gmv
                modeledGmv
                referenceEquity
                positions {
                  id
                  internalId
                  modelProviderId
                  bloombergTicker
                  assetClass
                  assetSubclass
                  description
                  country
                  currency
                  economicExposure
                  positionSetEconomicExposure
                  ${generateClassificationQueryForContributors("GICS", classifications)}
                }
              }
              exposure {
                factors {
                  id
                  value: net
                  name
                  category
                }
              }
              risk {
                date
                total
                attribution {
                  summary {
                    factors
                    specific
                  }
                  factors {
                    id
                    category
                    value
                  }
                }
              }
              predictedFactorSensitivity: factorSensitivity(${factorSensitivityParametersString(FACTOR_SENSITIVITY_TYPES.PREDICTED)}) {
                factors {
                  id
                  category
                  value: sensitivity
                }
              }
              historicalFactorSensitivity: factorSensitivity(${factorSensitivityParametersString(FACTOR_SENSITIVITY_TYPES.HISTORICAL)}) {
                factors {
                  id
                  category
                  value: sensitivity
                }
              }
              ${getOptimizationContentSetsQueryNodes(contentSets)}
            }
          }
        }`,
        variables: {
          modelId,
          portfolioId,
          on: selectedDate,
          base,
        },
      }
    },
  }, {
    resource: "forecastData",
    as: null,
    url: ({ modelId, portfolioId, selectedDate, viewType, base, baseSignature, horizon, selectedCustomForecast }) => {
      baseSignature = viewType === VIEW_TYPES.ACTIVE ? baseSignature : ""
      base = viewType === VIEW_TYPES.ACTIVE ? base : null

      return {
        id: `forecastData-${modelId}${portfolioId}${selectedDate}${horizon}${baseSignature}${selectedCustomForecast}`,
        query: `query ($modelId: String!, $portfolioId: String!, $on: Date!, $base: [PositionSetInput], $horizon: Int!) {
          model (id: $modelId) {
            simulation (positionSet: {
              id: $portfolioId,
              type: PORTFOLIO
            }, base: $base, from: $on, to: $on) {
              forecast(on: $on, horizon: $horizon) {
                forecastData: ${selectedCustomForecast ? `custom(id: "${selectedCustomForecast}")` : "impliedReturns (riskFactors: MARKET)"} {
                  horizon
                  total
                  equities {
                    id {
                      ticker
                      mic
                      modelProviderId
                    }
                    expectedPercentReturn
                  }
                  swaps { id expectedPercentReturn }
                }
              }
            }
          }
        }`,
        variables: {
          modelId,
          portfolioId,
          on: selectedDate,
          base,
          horizon,
        },
      }
    },
  }, {
    resource: "optimizationConstraintsPreferences",
    url: ({ accountId, portfolioUUID, modelId }) => {
      if (!portfolioUUID) {
        return null
      }

      return ({
        id: `optimizationConstraintsPreferences-${accountId}${portfolioUUID}${modelId}`,
        url: "/api/users/me/preferences",
        payload: {
          items: [
            {
              type: "optimizationConstraints",
              key: `${portfolioUUID}`,
              accountId,
            }, {
              type: "optimizationForecast",
              key: `${portfolioUUID}`,
              accountId,
            }, {
              type: "optimizationExposures",
              key: `${portfolioUUID}:${modelId}`,
              accountId,
            },
          ],
        },
      })
    },
  },
  MODEL_METADATA_QUERY,
  ],
  mapStateToProps: (state, props) => ({
    currentConstraints: getCurrentConstraints(state, props),
    apiConstraints: getApiConstraints(state, props),
    portfolioType: getPortfolioType(state, props),
    rebalanceUrl: getRebalanceUrl(state, props),
    tradesPanelOpen: get(state, "modules.optimization.tradesPanelOpen"),
    portfolioData: getPortfolioData(state, props),
    earliestDate: getEarliestDate(state, props),
    latestDate: getLatestDate(state, props),
    unmodeledDates: getUnmodeledDates(state, props),
    riskTarget: getRiskTarget(state, props),
    rebalanceTrades: getRebalanceTrades(state, props),
    trades: getTrades(state, props),
    experiment: getExperiment(state, props),
    allPositions: getAllPositions(state, props),
    portfolioNetLong: getPortfolioNetLong(state, props),
    activatedConstraints: getActivatedConstraints(state, props),
    experimentSecuritiesConstraints: getExperimentSecuritiesConstraints(state, props),
    selectedExperiment: getSelectedExperiment(state, props),
    isOpPro: getIsOpPro(state, props),
  }),
  view,
  actions: {
    ...Creators,
    loadTrades: RebalanceCreators.loadTrades,
    openDialog: DialogCreators.open,
    loadTradesConstraints: OptimizationTradesCreators.loadTradesConstraints,
    setExperimentSelectedDate: EditExperimentCreators.setDate,
  },
})
